'use client';

import { Briefcase, ExternalLink } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';

interface BusinessLiabilityGuardrailProps {
  opportunityId: string;
  businessLiabilityTotal: number;
}

function formatCurrency(amount: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);
}

const SF_BASE = 'https://taxrise.lightning.force.com/lightning/r/Opportunity';

export function BusinessLiabilityGuardrail({
  opportunityId,
  businessLiabilityTotal,
}: BusinessLiabilityGuardrailProps) {
  const salesforceUrl = `${SF_BASE}/${opportunityId}/view`;

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
      <Card className="max-w-lg w-full">
        <CardContent className="pt-6 text-center">
          <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Briefcase className="w-8 h-8 text-amber-600" />
          </div>

          <h2 className="text-xl font-semibold text-gray-900 mb-2">
            Business Tax Liabilities Detected
          </h2>

          <p className="text-gray-600 mb-4">
            This client has outstanding business tax liabilities that are not
            supported in the RED Experience. Please complete the resolution
            eligibility assessment manually in Salesforce.
          </p>

          {businessLiabilityTotal > 0 && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg px-4 py-3 mb-6">
              <p className="text-sm text-amber-700">
                Business liability total:{' '}
                <span className="font-semibold text-amber-900">
                  {formatCurrency(businessLiabilityTotal)}
                </span>
              </p>
            </div>
          )}

          <Button asChild size="lg" className="w-full">
            <a
              href={salesforceUrl}
              target="_blank"
              rel="noopener noreferrer"
            >
              Open in Salesforce
              <ExternalLink className="ml-2 h-4 w-4" />
            </a>
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}
